import type { FormEvent } from "react";
import { useEffect, useState } from "react";

import { Dialog } from "@/components/ui/Dialog";
import type { UserOut } from "@/lib/api";

interface Props {
  open: boolean;
  mode: "create" | "edit";
  user: UserOut | null;
  onClose: () => void;
  onSubmit: (payload: {
    name: string;
    email: string;
    password?: string;
    is_active: boolean;
    is_admin: boolean;
  }) => Promise<void>;
}

export function UserFormDialog({ open, mode, user, onClose, onSubmit }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (mode === "edit" && user) {
      setName(user.name);
      setEmail(user.email);
      setPassword("");
      setIsActive(user.is_active);
      setIsAdmin(user.is_admin);
    } else {
      setName("");
      setEmail("");
      setPassword("");
      setIsActive(true);
      setIsAdmin(false);
    }
  }, [open, mode, user]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Informe o nome.");
      return;
    }
    if (mode === "create") {
      if (!email.trim()) {
        setError("Informe o email.");
        return;
      }
      if (password.length < 8) {
        setError("Senha deve ter pelo menos 8 caracteres.");
        return;
      }
    }
    setSubmitting(true);
    try {
      if (mode === "create") {
        await onSubmit({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          password,
          is_active: isActive,
          is_admin: isAdmin,
        });
      } else if (user) {
        await onSubmit({
          name: name.trim(),
          email: user.email,
          is_active: isActive,
          is_admin: isAdmin,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível salvar.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      title={mode === "create" ? "Novo usuário" : "Editar usuário"}
      open={open}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-ink-600 px-4 py-2 text-sm font-medium text-ink-200 hover:bg-ink-800"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="user-admin-form"
            disabled={submitting}
            className="rounded bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 disabled:opacity-50"
          >
            {submitting ? "Salvando…" : "Salvar"}
          </button>
        </>
      }
    >
      <form id="user-admin-form" className="space-y-4" onSubmit={(e) => void handleSubmit(e)}>
        <div className="space-y-2">
          <label htmlFor="user-name" className="block text-xs font-medium text-ink-400">
            Nome
          </label>
          <input
            id="user-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            className="w-full rounded border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-ink-100 outline-none ring-violet-500/40 focus:ring-2"
          />
        </div>
        {mode === "create" && (
          <>
            <div className="space-y-2">
              <label htmlFor="user-email" className="block text-xs font-medium text-ink-400">
                Email
              </label>
              <input
                id="user-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="w-full rounded border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-ink-100 outline-none ring-violet-500/40 focus:ring-2"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="user-password" className="block text-xs font-medium text-ink-400">
                Senha inicial
              </label>
              <input
                id="user-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                className="w-full rounded border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-ink-100 outline-none ring-violet-500/40 focus:ring-2"
              />
            </div>
          </>
        )}
        {mode === "edit" && user && (
          <p className="text-xs text-ink-500">
            Email: <span className="text-ink-300">{user.email}</span> (não editável)
          </p>
        )}
        <div className="flex flex-col gap-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-200">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="rounded border-ink-600 bg-ink-900 text-violet-600"
            />
            Conta ativa
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-200">
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
              className="rounded border-ink-600 bg-ink-900 text-violet-600"
            />
            Administrador
          </label>
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
      </form>
    </Dialog>
  );
}
